'use client';

/**
 * /models — directory of every AI model in the registry.
 *
 * Acts as a discovery surface: pick a model → land on the home gallery
 * filtered to prompts that mention it. Reads from the live registry
 * (admin-managed via /admin → AI models tab) so additions appear here
 * without a redeploy.
 *
 * Layout per category:
 *   - Eyebrow: category label
 *   - Grid of cards: brand color stripe, name, brand, prompt-count
 *   - Hover: subtle lift + brand-tinted ring
 */
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import type { AIModel, AIModelCategory } from '@/lib/ai-models';
import { useAIModels } from '@/lib/ai-models-context';
import { api } from '@/lib/api';
import AdSlot from '@/components/ads/AdSlot';

const CATEGORY_ORDER: { key: AIModelCategory; label: string; eyebrow: string; description: string }[] = [
  { key: 'text',  label: 'Text & Chat',     eyebrow: 'CHAT',  description: 'General-purpose conversation, reasoning, and writing.' },
  { key: 'image', label: 'Image',           eyebrow: 'IMAGE', description: 'Generate stills, cover art, and visual experiments.' },
  { key: 'video', label: 'Video',           eyebrow: 'VIDEO', description: 'Motion synthesis — short clips and animation.' },
  { key: 'voice', label: 'Voice & Audio',   eyebrow: 'AUDIO', description: 'Speech, music, and audio generation.' },
  { key: 'code',  label: 'Code & Agents',   eyebrow: 'CODE',  description: 'Coding assistants and autonomous tool-use agents.' },
];

export default function ModelsDirectoryPage() {
  const { models, ready } = useAIModels();
  // Total prompt count (sitewide) — used for the hero ribbon. We fetch it
  // once and don't worry about per-model counts here; clicking a chip lands
  // on the gallery where the user can browse.
  const [totalPrompts, setTotalPrompts] = useState<number | null>(null);

  useEffect(() => {
    api.stats.public()
      .then((s) => setTotalPrompts(s.prompts_shared))
      .catch(() => { /* ignore */ });
  }, []);

  const grouped = useMemo(() => {
    const buckets: Record<string, AIModel[]> = {};
    for (const m of models) {
      const cat = m.category;
      (buckets[cat] = buckets[cat] || []).push(m);
    }
    // Sort within each bucket: by brand, then name
    for (const cat in buckets) {
      buckets[cat].sort((a, b) =>
        a.brand.localeCompare(b.brand) || a.name.localeCompare(b.name)
      );
    }
    return buckets;
  }, [models]);

  return (
    <>
      {/* Hero */}
      <section className="hero page-pad" style={{ paddingTop: 56, paddingBottom: 32, maxWidth: 1100 }}>
        <div style={{
          fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em',
          color: 'var(--ink-3)', marginBottom: 10, fontWeight: 600,
        }}>
          AI model directory
        </div>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontVariationSettings: '"opsz" 110, "SOFT" 50',
          fontSize: 'clamp(36px, 4.6vw, 60px)',
          fontWeight: 400,
          letterSpacing: '-0.03em',
          lineHeight: 1.05,
          margin: '0 0 14px',
          color: 'var(--ink)',
          textWrap: 'balance',
        }}>
          Every model{' '}
          <em style={{ fontStyle: 'italic', color: 'var(--accent)', fontWeight: 400 }}>
            we know about
          </em>
          .
        </h1>
        <p style={{
          fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.55,
          maxWidth: 620, margin: '0 0 8px',
        }}>
          {models.length} models across {Object.keys(grouped).length} categories
          {totalPrompts !== null ? `, powering ${totalPrompts.toLocaleString()} prompts in the gallery` : ''}.
          Pick one to find every prompt designed for it.
        </p>
      </section>

      {/* Sponsored slot — between the hero and the categorized list. */}
      <div className="page-pad" style={{ paddingBottom: 24, maxWidth: 1100 }}>
        <AdSlot slot="models-directory-top" format="horizontal" />
      </div>

      {/* Categories */}
      <div className="page-pad" style={{ paddingBottom: 80, maxWidth: 1400 }}>
        {!ready && models.length === 0 && (
          <div className="ps-empty">
            <h3>Loading models…</h3>
          </div>
        )}

        {CATEGORY_ORDER.map((cat) => {
          const bucket = grouped[cat.key] || [];
          if (bucket.length === 0) return null;
          return (
            <section key={cat.key} style={{ marginBottom: 56 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 14,
                marginBottom: 18, flexWrap: 'wrap',
              }}>
                <span style={{
                  fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em',
                  color: 'var(--accent)', fontWeight: 700,
                }}>
                  {cat.eyebrow}
                </span>
                <h2 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 24, fontWeight: 400,
                  letterSpacing: '-0.02em',
                  margin: 0,
                  color: 'var(--ink)',
                }}>
                  {cat.label}
                </h2>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {bucket.length} model{bucket.length === 1 ? '' : 's'}
                </span>
              </div>
              <p style={{
                fontSize: 13, color: 'var(--ink-3)',
                margin: '0 0 18px', maxWidth: 640,
              }}>
                {cat.description}
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 14,
              }}>
                {bucket.map((m) => (
                  <ModelCard key={m.id} model={m} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}

function ModelCard({ model }: { model: AIModel }) {
  return (
    <Link
      // Slug like `openai/gpt-4o` already contains a `/`. We don't encode it
      // because the dynamic route is a catch-all (`[...id]`) — the segments
      // become `params.id = ['openai', 'gpt-4o']`, rejoined as the slug.
      href={`/models/${model.id}`}
      title={`Open ${model.name} details`}
      className="no-underline"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '18px 18px 16px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-raised)',
        border: '1px solid var(--line)',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 6px 20px -8px ${model.color}44`;
        e.currentTarget.style.borderColor = `${model.color}55`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.borderColor = 'var(--line)';
      }}
    >
      {/* Brand color stripe */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, height: 3,
        background: model.color,
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 'var(--radius-sm)',
          display: 'grid', placeItems: 'center',
          background: `${model.color}18`,
          color: model.color,
          flexShrink: 0,
          fontFamily: 'var(--font-serif)',
          fontWeight: 600,
          fontSize: 14,
        }}>
          {model.icon ? (
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{model.icon}</span>
          ) : (
            model.brand[0]
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: 'var(--ink)',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {model.name}
          </div>
          <div style={{
            fontSize: 11.5, color: 'var(--ink-3)',
            marginTop: 1,
          }}>
            {model.brand}
          </div>
        </div>
      </div>

      <div style={{
        fontSize: 11, color: 'var(--ink-3)',
        fontFamily: 'var(--font-mono)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {model.id}
      </div>
    </Link>
  );
}
